// What a slides board shows in this browser: the slide open on each board (kept per browser, as a doc board keeps its
// page), the slides picked in the strip, whether the speaker notes are open, and the deck being presented.

import { stepMove } from "../../../shared/slideMotion";
import { slidesOf } from "../../../shared/slides";
import { boardEntries, get, toast } from "../../state/store";
import { create } from "zustand";
import type { ID } from "../../../shared/types";
import { readStoredJson, writeStoredJson } from "../../lib/stored";

interface SlidesUi {
  /** board id to the slide open on it */
  open: Record<ID, ID>;
  /** the slides picked in the strip, the open one among them */
  picked: ID[];
  /** the speaker notes under the slide, open or folded */
  notes: boolean;
}

const KEY = "cb:slides";

function load(): SlidesUi {
  const d = readStoredJson<Partial<SlidesUi>>(KEY, {});
  return { open: d.open && typeof d.open === "object" ? d.open : {}, picked: [], notes: d.notes === true };
}

export const useSlidesUi = create<SlidesUi>(() => load());

useSlidesUi.subscribe((s) => {
  writeStoredJson(KEY, { open: s.open, notes: s.notes }); // kept for this visit only when storage is out of reach
});

/** Opens a slide on its board, and picks it alone (or adds it to what is picked). */
export function openSlide(boardId: ID, id: ID, keepPicked = false) {
  useSlidesUi.setState((s) => ({ open: { ...s.open, [boardId]: id }, picked: keepPicked ? [...new Set([...s.picked, id])] : [id] }));
}

export function setPicked(ids: ID[]) {
  useSlidesUi.setState({ picked: ids });
}

export function setNotesOpen(on: boolean) {
  useSlidesUi.setState({ notes: on });
}

/** The slide open on a board: the one kept for it when it is still there, else the first. */
export function openOf<E extends { id: ID }>(slides: E[], kept: ID | undefined): E | null {
  return (kept && slides.find((s) => s.id === kept)) || slides[0] || null;
}

// ---------------------------------------------------------------------------
// presenting

export interface Presenting {
  boardId: ID;
  /** the slide on screen, by its place in the deck */
  at: number;
  /** the notes shown in this window (when the browser blocks the presenter's own) */
  notes: boolean;
  /** when the talk started, for the presenter's timer; paused: the time on it */
  started: number;
  paused: number | null;
  /** a shared link's visitor: no notes, nothing to change */
  readOnly: boolean;
  /** how many of the slide's builds show, whether the last came in just now, and which way the talk last moved (S7) */
  shown: number;
  fresh: boolean;
  dir: 1 | -1;
}

export const usePresent = create<{ p: Presenting | null }>(() => ({ p: null }));

/**
 * Presents a deck from a slide (its place in the whole deck). SC: a talk skips hidden slides, so it starts at the first
 * slide shown from there (the one before, at the deck's end), and `at` counts the slides shown.
 */
export function present(boardId: ID, at: number, readOnly = false) {
  const all = slidesOf(boardEntries(get(), boardId));
  const shown = all.filter((e) => e.data.hidden !== true);
  // every slide hidden: nothing to show, said, rather than a talk with no slide in it
  if (all.length && !shown.length) {
    toast({ level: "info", title: "Every slide is hidden", body: "Show a slide again (right-click it, Show slide) to present.", key: "all-hidden" });
    return;
  }
  const from = all.slice(Math.max(0, at)).find((e) => e.data.hidden !== true) ?? shown[shown.length - 1];
  const i = from ? shown.indexOf(from) : 0;
  usePresent.setState({ p: { boardId, at: Math.max(0, i), notes: false, started: Date.now(), paused: null, readOnly, shown: 0, fresh: false, dir: 1 } });
}

export function stopPresenting() {
  usePresent.setState({ p: null });
}

/** Moves the presented slide to one, within the deck, before any of its builds. */
export function presentTo(at: number, count: number) {
  usePresent.setState((s) => {
    if (!s.p) return s;
    const to = Math.max(0, Math.min(count - 1, at));
    return { p: { ...s.p, at: to, shown: 0, fresh: false, dir: to < s.p.at ? -1 : 1 } };
  });
}

/** A click on or back (S7): the slide's next build before the next slide; back to a slide shows it as it ended. */
export function presentMove(dir: 1 | -1, counts: number[]) {
  usePresent.setState((s) => {
    if (!s.p) return s;
    const to = stepMove({ at: s.p.at, shown: s.p.shown }, dir, counts);
    if (to.at === s.p.at && to.shown === s.p.shown) return s;
    return { p: { ...s.p, at: to.at, shown: to.shown, fresh: dir > 0 && to.at === s.p.at, dir } };
  });
}

export function togglePresentNotes() {
  usePresent.setState((s) => (s.p ? { p: { ...s.p, notes: !s.p.notes } } : s));
}

/** The presenter's timer, paused or going again. */
export function pauseTimer() {
  usePresent.setState((s) => {
    if (!s.p) return s;
    if (s.p.paused !== null) return { p: { ...s.p, started: Date.now() - s.p.paused, paused: null } };
    return { p: { ...s.p, paused: Date.now() - s.p.started } };
  });
}

export function resetTimer() {
  usePresent.setState((s) => (s.p ? { p: { ...s.p, started: Date.now(), paused: s.p.paused === null ? null : 0 } } : s));
}
